// client/src/lib/api/suppliers-api.ts
// ===========================================
// MÓDULO DE GESTIÓN DE PROVEEDORES
// Maneja CRUD, análisis, exportación y recordatorios de proveedores.
// ===========================================

import { apiRequest, buildUrlWithParams } from "./api-client";
import type { ApiMessageResponse } from "./types";

// -------------------------------------------
// Obtener proveedores de una tienda
// -------------------------------------------
export async function getShopSuppliers(shopId: string): Promise<{
  success: boolean;
  suppliers: any[];
  count: number;
}> {
  return apiRequest(`/api/shops/${shopId}/suppliers`);
}

// -------------------------------------------
// Obtener análisis de proveedores
// -------------------------------------------
export async function getSuppliersAnalysis(shopId: string): Promise<{
  success: boolean;
  suppliers: any[];
  stats: any;
  topSuppliersByProducts: any[];
  topSuppliersByMargin: any[];
  metadata: any;
}> {
  return apiRequest(`/api/shops/${shopId}/suppliers/analysis`);
}

// -------------------------------------------
// Obtener un proveedor específico
// -------------------------------------------
export async function getSupplier(id: string): Promise<{
  success: boolean;
  supplier: any;
}> {
  return apiRequest(`/api/suppliers/${id}`);
}

// -------------------------------------------
// Obtener estadísticas de un proveedor
// -------------------------------------------
export async function getSupplierStats(id: string): Promise<{
  success: boolean;
  supplier: any;
  productCount: number;
  totalInventoryValue: number;
  averageMargin: number;
  inventoryItems: any[];
  topProducts: any[];
}> {
  return apiRequest(`/api/suppliers/${id}/stats`);
}

// -------------------------------------------
// Crear un nuevo proveedor
// -------------------------------------------
export async function createSupplier(supplierData: {
  shopId: string;
  name: string;
  contactName?: string;
  email?: string;
  phone?: string;
  address?: string;
  taxId?: string;
  paymentTerms?: string;
  deliveryTime?: number;
  rating?: number;
  notes?: string;
}): Promise<{
  success: boolean;
  supplier: any;
  message: string;
}> {
  return apiRequest("/api/suppliers", {
    method: "POST",
    body: JSON.stringify(supplierData),
  });
}

// -------------------------------------------
// Actualizar un proveedor
// -------------------------------------------
export async function updateSupplier(
  id: string,
  updates: {
    name?: string;
    contactName?: string;
    email?: string;
    phone?: string;
    address?: string;
    taxId?: string;
    paymentTerms?: string;
    deliveryTime?: number;
    rating?: number;
    notes?: string;
    isActive?: boolean;
  },
): Promise<{
  success: boolean;
  supplier: any;
  message: string;
}> {
  return apiRequest(`/api/suppliers/${id}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

// -------------------------------------------
// Eliminar (inactivar) un proveedor
// -------------------------------------------
export async function deleteSupplier(id: string): Promise<ApiMessageResponse> {
  return apiRequest(`/api/suppliers/${id}`, {
    method: "DELETE",
  });
}

// -------------------------------------------
// Buscar proveedores
// -------------------------------------------
export async function searchSuppliers(
  shopId: string,
  query: string,
): Promise<{
  success: boolean;
  suppliers: any[];
  count: number;
  query: string;
}> {
  const url = buildUrlWithParams(`/api/shops/${shopId}/suppliers/search`, {
    q: query,
  });
  return apiRequest(url);
}

// -------------------------------------------
// Obtener productos de un proveedor específico
// -------------------------------------------
export async function getSupplierProducts(supplierId: string): Promise<{
  success: boolean;
  products: any[];
  count: number;
  supplier: any;
}> {
  return apiRequest(`/api/suppliers/${supplierId}/products`);
}

// -------------------------------------------
// Exportar proveedores a CSV (Descarga de archivo)
// -------------------------------------------
// COMENTARIO: Se mantiene `fetch` nativo para manejar la descarga de blobs/archivos,
// ya que `apiRequest` está optimizado para respuestas JSON tipadas.
export async function exportSuppliersCSV(shopId: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  try {
    const response = await fetch(`/api/shops/${shopId}/suppliers/export/csv`);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Error al exportar CSV");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `proveedores_${shopId}_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    return { success: true, message: "Archivo CSV descargado" };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
}

// -------------------------------------------
// Exportar proveedores para WhatsApp
// -------------------------------------------
// COMENTARIO: Mantenemos el try/catch para retornar { success: false } en lugar de lanzar error,
// preservando el comportamiento original que el frontend espera.
export async function exportSuppliersWhatsApp(shopId: string): Promise<{
  success: boolean;
  data?: string;
  message?: string;
  error?: string;
}> {
  try {
    const response = await apiRequest<{ message: string }>(
      `/api/shops/${shopId}/suppliers/export/whatsapp`,
    );
    return {
      success: true,
      data: response.message,
      message: "Contactos listos para WhatsApp",
    };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
}

// -------------------------------------------
// Enviar recordatorios a proveedores
// -------------------------------------------
export async function sendSupplierReminders(
  shopId: string,
  supplierIds: string[],
  message: string,
): Promise<{
  success: boolean;
  message: string;
  error?: string;
}> {
  try {
    const response = await apiRequest<{ message: string }>(
      `/api/shops/${shopId}/suppliers/reminders`,
      {
        method: "POST",
        body: JSON.stringify({ supplierIds, message }),
      },
    );
    return { success: true, message: response.message };
  } catch (error: any) {
    return {
      success: false,
      message: "Error al enviar recordatorios",
      error: error.message,
    };
  }
}
